import axios, { AxiosInstance } from 'axios';
import keycloak from '../config/keycloak';
import { getEnv } from '../utils/env';

interface DangerousGoodsResponse {
  cargo: string;
  modelVersion?: string;
  isDangerous: boolean;
  confidence?: number;
  probability?: number;
  classification?: string;
  // v5 only: a UN number with no exempting special provision forced the verdict
  unRuleDg?: boolean;
  // False when the model never ran and the probability is the customer's prior
  scored?: boolean;
  reasons?: string[];
  // DGML's own answer for this cargo, carried so a flagged result is reported as the
  // model gave it rather than as the card's rounding of it
  raw?: unknown;
}

// One IMDG entry for a UN number as Hazcheck Validate lists it; a UN number has
// one per variant (a packing group, a state, a concentration band).
interface ValidateEntry {
  substanceId: string;
  properShippingName: string;
  classes: string[];
  packingGroup: string | null;
  variant: string | null;
  labels: string[];
  url: string;
}

interface ValidateLookup {
  unNumber: string;
  regulation: string;
  searchUrl: string;
  // Empty when the predicted number does not exist in the regulation
  entries: ValidateEntry[];
}

interface UNPrediction {
  prediction: string;
  probability: number;
  // Present on the top predictions only; null when Validate could not be reached
  validate?: ValidateLookup | null;
}

interface UNNumbersResponse {
  cargo: string;
  modelVersion?: string;
  unNumbers: UNPrediction[];
  topPrediction: UNPrediction | null;
  raw?: unknown;
}

// One of the model's own suggestions. `probability` is the softmax of cosine over
// every code the model can rank: it orders exactly as cosine does and is not
// calibrated against outcomes. `candidate` marks a declared code it ranked itself.
interface HSPrediction {
  hs6: string;
  hs4: string;
  hs2: string;
  cosine: number;
  probability: number;
  definition?: string | null;
  candidate?: boolean;
}

// A declared code DGML was asked to score. Unscored means it was not six digits
// (hs6 is then the string as sent, with no hs4 or hs2), the model has no prototype
// for it, or the text had nothing to encode; its probability of 0 is a placeholder.
// `rank` is its 1-based place among the suggestions, null when it is not among them.
interface HSCandidate {
  hs6: string;
  hs4: string | null;
  hs2: string | null;
  cosine: number | null;
  probability: number;
  definition: string | null;
  scored: boolean;
  rank: number | null;
}

interface HSCodesResponse {
  cargo: string;
  modelVersion: string;
  scored: boolean;
  predictions: HSPrediction[];
  candidates: HSCandidate[];
  raw?: unknown;
}

// One requested field after preprocessing. v5 gives the masked lines that field
// contributes on its own; v4 gives the cleaned texts and no lines.
interface PreprocessedField {
  field: string;
  lines: string[] | null;
  cleanedText: string | null;
  dgText: string | null;
  unText: string | null;
}

// What the model would be given for a text, with no scoring. `lines` is the v5
// cargo-level input to the encoder (null for v4); empty means nothing survived
// masking and a v5 score would fall back to the customer prior.
interface PreprocessingResponse {
  cargo: string;
  modelVersion: string;
  lines: string[] | null;
  unRuleDg: boolean;
  fields: PreprocessedField[];
  raw?: unknown;
}

// One DGML field in a booking-derived request: the live job's field names (a
// description's source, un-number, hs-code, or a top-level booking key).
interface BookingField {
  field: string;
  text: string;
}

interface FieldVerdict {
  field: string;
  probability: number;
  scored: boolean;
}

// The DG verdict for one scored item, whichever endpoint scored it
interface DgVerdict {
  probability: number;
  prediction: boolean;
  unRuleDg: boolean;
  scored: boolean;
  fieldResults: FieldVerdict[];
}

// One cargo item of a booking, scored by hcd_prediction. `unNumbers` is null when
// the item was not predicted DG, because DGML only runs the UN model on those.
interface BookingCargoResult extends DgVerdict {
  id: string;
  ctuIndex: number;
  cargoIndex: number;
  ctuId: string | null;
  declaredDg: boolean;
  fields: BookingField[];
  unNumbers: UNPrediction[] | null;
  raw?: unknown;
}

// The booking's top-level text scored as one item by dg_prediction; null when the
// booking had no such text.
interface BookingLevelResult extends DgVerdict {
  fields: BookingField[];
  raw?: unknown;
}

interface BookingClassificationResponse {
  modelVersion: string;
  customer: string | null;
  reference: string | null;
  declarations: { hasDg: boolean; hasNonDg: boolean };
  cargo: BookingCargoResult[];
  booking: BookingLevelResult | null;
}

interface EmbeddingItem {
  id: string;
  text: string;
  embedding: number[] | null;
  dimensions: number;
}

interface EmbeddingsResponse {
  modelVersion: string;
  items: EmbeddingItem[];
}

// Model keys as DGML names them: dg-v5, un-v1, hs-v1, emb-v3
interface ModelsResponse {
  enabled: string[];
  known: string[];
}

// A DGML deployment the backend can forward to, e.g. prod and dev
interface DgmlTarget {
  name: string;
  url: string;
}

interface TargetsResponse {
  default: string;
  targets: DgmlTarget[];
}

// Which model a flagged result came from. Each states its correction in its own
// terms, because a wrong dangerous-goods verdict, a wrong UN number and a wrong HS
// code are three different statements.
type FeedbackFamily = 'dg' | 'un' | 'hs';

// isDg null is not a missing answer: it is the reporter saying the text does not
// determine it, which is a verdict the benchmark records in its own right.
type FeedbackVerdict =
  | { isDg: boolean | null }
  | { unNumbers: string[] }
  | { hsCode: string };

interface FeedbackReport {
  model: FeedbackFamily;
  modelVersion: string;
  cargo: string;
  response?: unknown;
  verdict: FeedbackVerdict;
  note?: string;
}

interface FeedbackResponse {
  success: boolean;
  reportId: string;
  cargoUid: string;
  reportedAt: string;
  storedIn: 'blob' | 'local';
}

interface StoredFeedback {
  reportId: string;
  cargoUid: string;
  reportedAt: string;
  cargo: string;
  model: { family: FeedbackFamily; version: string; key: string };
  verdict: FeedbackVerdict;
  note: string | null;
}

class ApiService {
  private backendApi: AxiosInstance;
  private readonly API_URL = getEnv('REACT_APP_API_URL', '');
  // Which DGML deployment the backend forwards to; every call carries it
  private target = 'prod';

  constructor() {
    // Create axios instance for our backend API
    this.backendApi = axios.create({
      baseURL: this.API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include user's Keycloak token
    this.backendApi.interceptors.request.use(
      async (config) => {
        // Get user's authentication token
        if (keycloak?.token) {
          config.headers.Authorization = `Bearer ${keycloak.token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for token refresh on 401
    this.backendApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Try to refresh the user token
          if (keycloak?.authenticated) {
            try {
              const refreshed = await keycloak.updateToken(5);
              if (refreshed && error.config) {
                error.config.headers.Authorization = `Bearer ${keycloak.token}`;
                return this.backendApi.request(error.config);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              // Redirect to login if refresh fails
              keycloak.login();
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private requireLogin() {
    if (!keycloak?.authenticated) {
      throw new Error('User not authenticated');
    }
  }

  getTarget(): string {
    return this.target;
  }

  setTarget(target: string) {
    this.target = target;
  }

  async getTargets(): Promise<TargetsResponse> {
    this.requireLogin();

    const response = await this.backendApi.get('/api/v1/dgml/targets');
    return response.data;
  }

  async getModels(): Promise<ModelsResponse> {
    this.requireLogin();

    const response = await this.backendApi.get('/api/v1/dgml/models', { params: { target: this.target } });
    return response.data;
  }

  async classifyDangerousGoods(cargoText: string, modelVersion: string = 'v4'): Promise<DangerousGoodsResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/classify', {
      cargo: cargoText,
      modelVersion: modelVersion,
      target: this.target
    });

    return response.data;
  }

  // The booking is the parsed JSON object; the backend picks the fields the live
  // job would send and scores cargo items and booking text separately.
  async classifyBooking(booking: object, modelVersion: string, customer?: string, topK?: number): Promise<BookingClassificationResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/classify-booking', {
      booking,
      modelVersion,
      customer: customer || undefined,
      topK,
      target: this.target
    });

    return response.data;
  }

  async classifyMultipleCargo(cargoList: string[]): Promise<DangerousGoodsResponse[]> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/classify/batch', {
      cargo: cargoList,
      target: this.target
    });

    return response.data.cargo;
  }

  async healthCheck(): Promise<any> {
    const response = await this.backendApi.get('/api/v1/dgml/health');
    return response.data;
  }

  async getUserProfile(): Promise<any> {
    const response = await this.backendApi.get('/api/v1/user/profile');
    return response.data;
  }

  async classifyUNNumbers(cargoText: string, modelVersion: string = 'v1', topK?: number): Promise<UNNumbersResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/classify-un', {
      cargo: cargoText,
      modelVersion: modelVersion,
      topK,
      target: this.target
    });

    return response.data;
  }

  // `declared` is the HS codes to score beside the model's suggestions, as typed:
  // the backend normalises them the way the live HS job does.
  async classifyHSCodes(cargoText: string, modelVersion: string = 'v1', declared: string[] = []): Promise<HSCodesResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/classify-hs', {
      cargo: cargoText,
      modelVersion: modelVersion,
      candidates: declared.length > 0 ? declared : undefined,
      target: this.target
    });

    return response.data;
  }

  async preprocessCargo(cargoText: string, modelVersion: string = 'v5'): Promise<PreprocessingResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/preprocess', {
      cargo: cargoText,
      modelVersion: modelVersion,
      target: this.target
    });

    return response.data;
  }

  async embedCargo(cargoTexts: string[], modelVersion: string = 'v3'): Promise<EmbeddingsResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/embed', {
      cargo: cargoTexts,
      modelVersion: modelVersion,
      target: this.target
    });

    return response.data;
  }

  async submitFeedback(report: FeedbackReport): Promise<FeedbackResponse> {
    this.requireLogin();

    const response = await this.backendApi.post('/api/v1/dgml/feedback', {
      ...report,
      target: this.target
    });

    return response.data;
  }

  async getRecentFeedback(): Promise<StoredFeedback[]> {
    this.requireLogin();

    const response = await this.backendApi.get('/api/v1/dgml/feedback/recent');
    return response.data.reports;
  }
}

const apiService = new ApiService();

export default apiService;
export type {
  DangerousGoodsResponse,
  UNPrediction,
  UNNumbersResponse,
  ValidateEntry,
  ValidateLookup,
  HSPrediction,
  HSCandidate,
  HSCodesResponse,
  PreprocessedField,
  PreprocessingResponse,
  BookingField,
  FieldVerdict,
  DgVerdict,
  BookingCargoResult,
  BookingLevelResult,
  BookingClassificationResponse,
  EmbeddingItem,
  EmbeddingsResponse,
  ModelsResponse,
  DgmlTarget,
  TargetsResponse,
  FeedbackFamily,
  FeedbackVerdict,
  FeedbackReport,
  FeedbackResponse,
  StoredFeedback,
};
